import { supabase } from '../lib/supabase';
import { captureAppException } from './errorCaptureUtils';

/**
 * Reference to a file in Supabase storage
 */
export interface FileReference {
  bucket: string;
  path: string;
}

/**
 * Upload or replace a file in Supabase storage
 * 
 * @param fileRef - Reference to where the file should be stored
 * @param file - The file to upload
 * @param onSuccess - Callback function called with file reference on successful upload
 * @param onError - Callback function called with full error on failure
 * 
 * @example
 * ```tsx
 * // Upload a community header image
 * const fileRef: FileReference = {
 *   bucket: 'community_images',
 *   path: 'public/123/header.png'
 * };
 * 
 * uploadFile(
 *   fileRef,
 *   file,
 *   (updatedFileRef) => {
 *     // Get public URL and update state
 *     const publicUrl = supabase.storage.from(updatedFileRef.bucket).getPublicUrl(updatedFileRef.path).data.publicUrl;
 *     setHeaderImage(publicUrl);
 *   },
 *   (error) => {
 *     // Handle error
 *     console.error('Upload failed:', error);
 *     toast.error(error.message);
 *   }
 * );
 * ```
 */
export const uploadFile = async (
  fileRef: FileReference,
  file: File,
  onSuccess: (updatedFileRef: FileReference) => void,
  onError: (error: Error) => void
): Promise<void> => {
  try {
    const { error: uploadError } = await supabase.storage
      .from(fileRef.bucket)
      .upload(fileRef.path, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) throw uploadError;
    onSuccess(fileRef);
  } catch (err) {
    captureAppException(err, {
      tags: { context: 'file_upload', bucket: fileRef.bucket },
      extra: { path: fileRef.path, fileName: file.name, fileSize: file.size, fileType: file.type }
    });
    onError(err as Error);
  }
};

/**
 * Remove a file from Supabase storage
 * 
 * @param fileRef - Reference to the file to be removed
 * @param onSuccess - Callback function called on successful removal
 * @param onError - Callback function called with full error on failure
 * 
 * @example
 * ```tsx
 * // Remove a community header image
 * const fileRef: FileReference = {
 *   bucket: 'community_images',
 *   path: 'public/123/header.png'
 * };
 * 
 * removeFile(
 *   fileRef,
 *   () => {
 *     // Update state or database
 *     setHeaderImage('');
 *   },
 *   (error) => {
 *     // Handle error
 *     console.error('Remove failed:', error);
 *     toast.error(error.message);
 *   }
 * );
 * ```
 */
export const removeFile = async (
  fileRef: FileReference,
  onSuccess: () => void,
  onError: (error: Error) => void
): Promise<void> => {
  try {
    const { error } = await supabase.storage
      .from(fileRef.bucket)
      .remove([fileRef.path]);

    if (error) throw error;
    onSuccess();
  } catch (err) {
    captureAppException(err, {
      tags: { context: 'file_remove', bucket: fileRef.bucket },
      extra: { path: fileRef.path }
    });
    onError(err as Error);
  }
};

/**
 * Get public asset URL from Supabase storage
 * 
 * @param assetPath - Path to the asset in the public storage (e.g., 'github-logo.svg', 'icons/discord-icon.svg')
 * @param bucket - Storage bucket name (defaults to 'assets')
 * @returns The full URL to the asset, or null if supabase URL not available
 * 
 * @example
 * ```tsx
 * const githubIcon = getPublicAssetUrl('github-logo.svg');
 * const customIcon = getPublicAssetUrl('icons/custom-icon.png', 'custom-bucket');
 * ```
 */
export const getPublicAssetUrl = (assetPath: string, bucket: string = 'assets'): string | null => {
  try {
    const { data } = supabase.storage.from(bucket).getPublicUrl(assetPath);
    return data?.publicUrl || null;
  } catch (error) {
    captureAppException(error, { tags: { context: 'get_public_asset_url', asset_path: assetPath, bucket } });
    return null;
  }
};

/**
 * Get the public URL for a file in Supabase storage
 * 
 * @param fileRef - Reference to the file
 * @returns The public URL for the file, or null if path is invalid or error occurs
 * 
 * @example
 * ```tsx
 * const fileRef: FileReference = {
 *   bucket: 'community_images',
 *   path: 'public/123/header.png'
 * };
 * 
 * const publicUrl = getPublicUrl(fileRef);
 * ```
 */
export const getPublicUrl = (fileRef: FileReference): string | null => {
  if (!fileRef.path || !fileRef.path.includes('/')) {
    captureAppException(new Error('Invalid file path'), {
      tags: { context: 'get_public_url', bucket: fileRef.bucket },
      extra: { path: fileRef.path }
    });
    return null;
  }

  try {
    const { data } = supabase.storage.from(fileRef.bucket).getPublicUrl(fileRef.path);
    return data.publicUrl;
  } catch (err) {
    captureAppException(err, {
      tags: { context: 'get_public_url', bucket: fileRef.bucket },
      extra: { path: fileRef.path }
    });
    return null;
  }
}; 